import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth-service';
import { PermissionChecker, canAccessAdminPanel } from '../auth/permissions';
import { getAccessibleTools } from '../tools/tool-registry';

export const REQUEST_ACCESS_PATH = '/request-access';

export function resolvePostLoginPath(checker: PermissionChecker): string {
  const accessibleTools = getAccessibleTools(checker);
  if (accessibleTools.length >= 2) {
    return '/tool-picker';
  }
  if (accessibleTools.length === 1) {
    return accessibleTools[0].homePath;
  }
  if (canAccessAdminPanel(checker)) {
    return '/admin-panel';
  }
  return REQUEST_ACCESS_PATH;
}

async function ensureAuthenticatedWithClaims(authService: AuthService): Promise<boolean> {
  await authService.waitForAuthState();

  if (!authService.isAuthenticated()) {
    return false;
  }

  await authService.waitForClaims();

  return authService.isAuthenticated();
}

export const authGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.waitForAuthState();

  if (authService.isAuthenticated()) {
    return true;
  }

  router.navigate(['/login']);
  return false;
};

export const roleRedirectGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!(await ensureAuthenticatedWithClaims(authService))) {
    return router.createUrlTree(['/login']);
  }

  return router.createUrlTree([resolvePostLoginPath(authService)]);
};

export const toolPickerGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!(await ensureAuthenticatedWithClaims(authService))) {
    return router.createUrlTree(['/login']);
  }

  const accessibleTools = getAccessibleTools(authService);
  if (accessibleTools.length === 0) {
    return router.createUrlTree([resolvePostLoginPath(authService)]);
  }

  return true;
};

export const publicGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!(await ensureAuthenticatedWithClaims(authService))) {
    return true;
  }

  return router.createUrlTree([resolvePostLoginPath(authService)]);
};

export const requestAccessGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!(await ensureAuthenticatedWithClaims(authService))) {
    router.navigate(['/login']);
    return false;
  }

  const targetPath = resolvePostLoginPath(authService);
  if (targetPath !== REQUEST_ACCESS_PATH) {
    return router.createUrlTree([targetPath]);
  }

  return true;
};
